import { Anchor, Group, Modal, Stack, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';
import { useState } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { ToolbarExportButton } from '@apple/utils/files/components/ToolbarExportButton';

import { getBudgetSummaryExportUrl } from '@apple/features/store/api/budget';
import type { BudgetItem } from '@apple/features/store/components/BudgetSummary';

import type { BudgetDetailsItem } from '../models/budget';
import { PurchaseSummaryTable } from './PurchaseSummaryTable';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export interface BudgetDetailsTableProps {
	fiscalYear: number;
	singleStoreCustomerCode: string;
	periodNumber: number;
	budgetDetailData: BudgetDetailsItem[];
	isLoading: boolean;
	isFetching: boolean;
	budgetItems?: BudgetItem[];
}

export function BudgetDetailsTable({
	fiscalYear,
	singleStoreCustomerCode,
	periodNumber,
	budgetDetailData,
	isLoading,
	isFetching,
	budgetItems,
}: BudgetDetailsTableProps) {
	const { t } = useTranslation('store');
	const [opened, { open, close }] = useDisclosure(false);
	const [categoryId, setCategoryId] = useState<number | null>(null);

	const handleCategoryClick = (selectedCategory: number) => {
		setCategoryId(selectedCategory);
		open();
	};

	const table = useTable({
		features,
		data: budgetDetailData ?? [],
		enableSorting: true,
		columnResizeMode: 'onChange',
		enableFilters: false,
		enableColumnFilters: false,
		manualFiltering: false,
		manualSorting: false,
		columns: [
			{
				accessorKey: 'categoryName',
				header: t('budgets.budgetDetails.headers.category'),
				cell: ({ row }) => (
					<Anchor onClick={() => handleCategoryClick(row.original.categoryId)}>
						{row.original.categoryName}
					</Anchor>
				),
			},
			{
				accessorKey: 'amountSpent',
				header: t('budgets.budgetDetails.headers.spent'),
			},
			{
				accessorKey: 'formattedPercentOfTotalBudget',
				header: () => (
					<Group grow justify='space-between'>
						{t('budgets.budgetDetails.headers.budgetpercentage')}
						<Group justify='flex-end'>
							<ToolbarExportButton
								data-testid='download-budget-detail'
								key='budget-details'
								tooltip={t('budgets.budgetSummary.tooltip')}
								exportUrl={getBudgetSummaryExportUrl(
									singleStoreCustomerCode,
									fiscalYear,
								)}
								fallbackFilename='BudgetTransactionReport.xlsx'
								filters={budgetItems}
							/>
						</Group>
					</Group>
				),
			},
		],
	});

	function renderEmpty() {
		return (
			<Stack align='center'>
				<Title order={3}>{t('common:pagination:empty')}</Title>
			</Stack>
		);
	}

	return (
		<>
			<DataTable
				hidePageSize
				hidePagination
				table={table}
				variant='apple-table'
				minWidth='300px'
				renderEmpty={renderEmpty}
				loading={isFetching || isLoading}
			/>

			<Modal
				size='auto'
				opened={opened}
				onClose={close}
				title={t('budgets.purchaseSummary.title')}
				id='purchase-summary'
			>
				{categoryId && (
					<PurchaseSummaryTable
						singleStoreCustomerCode={singleStoreCustomerCode}
						periodNumber={periodNumber}
						categoryId={categoryId}
						fiscalYear={fiscalYear}
					/>
				)}
			</Modal>
		</>
	);
}
